import { useId, type ChangeEvent } from 'react'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { ACCEPTED_TYPES, validateLogoFile } from './logoFile'
import './merchants.css'

/**
 * The file control, shared by both merchant forms — #468.
 *
 * It picks and VALIDATES; it never uploads. That split is what lets the same
 * control serve two callers whose next step is completely different: the edit
 * path (`MerchantLogoField`) has a merchant id and POSTs immediately, while
 * the create path (`MerchantForm`) has no id yet and must hold the bytes until
 * `POST /merchants` has answered with one.
 *
 * `onPick` receives only files that passed `validateLogoFile`; `onReject`
 * receives the message for the ones that did not. A caller therefore cannot
 * accidentally skip the check, and neither caller restates the limits.
 */
export function LogoPicker({
  pending,
  busyLabel,
  onPick,
  onReject,
}: {
  pending: boolean
  /** What the affordance reads while the caller is busy with the last pick. */
  busyLabel: string
  onPick: (file: File) => void
  onReject: (message: string) => void
}) {
  const inputId = useId()
  /* `'form'`, the same density the surrounding fields use. Written INLINE
     rather than as a media query for the reason `hitTarget.ts` records: #90
     measured an inline height beating a query at an emulated 375px. The box
     the hit-target sweep measures is the INPUT, which is stretched over this
     label by `inset: 0`, so the floor has to land on the label. */
  const rowHeight = useHitTarget('form')

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Cleared unconditionally so the SAME file can be re-picked after a
    // rejection — a browser does not fire `change` a second time for an
    // unchanged `value`.
    e.target.value = ''
    if (file === undefined) return
    const problem = validateLogoFile(file)
    if (problem !== undefined) {
      onReject(problem)
      return
    }
    onPick(file)
  }

  return (
    /* The visible affordance is the LABEL, skinned in `merchants.css`; the
       input itself is visually hidden — architect I9.

       A bare `<input type="file">` draws its button and its "no file chosen"
       text in OS chrome, and neither carries a declaration a colour or
       contrast sweep can read. This module refuses a native `<select>` and a
       native checkbox for exactly that reason, twice in its own code, and
       then shipped this. An absent declaration is unscannable and this repo
       has already shipped that defect twice.

       A `<label htmlFor>` wrapping the affordance keeps the whole keyboard and
       screen-reader contract the native control gives — clicking or pressing
       the label opens the picker, the input stays in the tab order and the
       accessibility tree — with no `.click()` shim. `opacity: 0` with
       `inset: 0`, not `display: none`, for the reason `iconGrid.css` records:
       the hit-target sweep measures the INPUT, so it has to remain a real,
       floor-sized box. */
    <label className="merchant-logo__label" htmlFor={inputId} style={{ minHeight: rowHeight }}>
      Logo
      <input
        id={inputId}
        className="merchant-logo__input"
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        disabled={pending}
        onChange={onChange}
      />
      <span className="merchant-logo__choose" aria-hidden="true">
        {pending ? busyLabel : 'Choose image'}
      </span>
    </label>
  )
}
